import React, { useRef, useState, VFC } from 'react';

import { KnowledgeBaseJournalEntry } from 'containers/ChatAgent/types';

import { CLOSE_ICON_SVG } from './icons';
import { useAgentContextStyles } from './styles';

interface VersionHistoryPanelProps {
  entries: KnowledgeBaseJournalEntry[];
  isLoading: boolean;
  activeEntryUuid: string | null;
  onEntryClick: (uuid: string) => void;
  onClose: () => void;
  onRestore: (journalUuid: string) => void;
  isRestoring: boolean;
  actorNames: Record<string, string>;
}

const OPERATION_LABELS: Record<KnowledgeBaseJournalEntry['operation'], string> =
  {
    update: 'Edited',
    create: 'Added',
    delete: 'Deleted',
    restore: 'Restored',
  };

const formatEntryDate = (isoString: string): string => {
  const d = new Date(isoString);
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const hours = d.getHours();
  const mins = d.getMinutes();
  const ampm = hours < 12 ? 'am' : 'pm';
  const h12 = hours % 12 || 12;
  const mm = mins === 0 ? '' : `:${String(mins).padStart(2, '0')}`;
  return `${month} ${day}, ${h12}${mm}${ampm}`;
};

const resolveActor = (
  createdBy: string | null,
  actorNames: Record<string, string>,
): string => {
  if (!createdBy) return 'Agent';
  return actorNames[createdBy] ?? createdBy;
};

const getTopicName = (entry: KnowledgeBaseJournalEntry): string => {
  if (entry.operation === 'delete') return entry.old_name ?? '—';
  return entry.new_name ?? entry.old_name ?? '—';
};

// SVG icons
const THREE_DOTS_SVG = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="3" r="1.2" fill="currentColor" />
    <circle cx="8" cy="8" r="1.2" fill="currentColor" />
    <circle cx="8" cy="13" r="1.2" fill="currentColor" />
  </svg>
);


// ─── Diff section ────────────────────────────────────────────────────────────

interface DiffField {
  label: string;
  oldValue: string | null;
  newValue: string | null;
  changed: boolean;
}

/**
 * Returns the fields to diff for an entry.
 * For the "restore to" preview we show what the FAQ will look like after restore.
 */
const buildDiffFields = (entry: KnowledgeBaseJournalEntry): DiffField[] => {
  const nameChanged =
    entry.old_name !== null &&
    entry.new_name !== null &&
    entry.old_name !== entry.new_name;
  const contentChanged =
    entry.old_content !== null &&
    entry.new_content !== null &&
    entry.old_content !== entry.new_content;

  return [
    {
      label: 'Question',
      oldValue: entry.old_name,
      newValue: entry.new_name,
      changed: nameChanged,
    },
    {
      label: 'Answer',
      oldValue: entry.old_content,
      newValue: entry.new_content,
      changed: contentChanged,
    },
  ];
};

/** The content the FAQ will have after this version is restored. */
const getRestoreTarget = (entry: KnowledgeBaseJournalEntry) => ({
  name: entry.operation === 'delete' ? entry.old_name : entry.new_name,
  content: entry.operation === 'delete' ? entry.old_content : entry.new_content,
});

interface DiffCardProps {
  entry: KnowledgeBaseJournalEntry;
  classes: ReturnType<typeof useAgentContextStyles>;
}

const DiffCard: VFC<DiffCardProps> = ({ entry, classes }) => {
  const fields = buildDiffFields(entry);
  const restoreTarget = getRestoreTarget(entry);

  // ── CREATE ──────────────────────────────────────────────────────────────
  if (entry.operation === 'create') {
    return (
      <div className={classes.versionHistoryDiff}>
        <div className={classes.versionHistoryDiffHeader}>Added</div>
        <div className={classes.versionHistoryDiffBody}>
          {entry.new_name && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>Question</span>
              <span className={classes.versionHistoryDiffAdded}>{entry.new_name}</span>
            </div>
          )}
          {entry.new_content && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>Answer</span>
              <span className={classes.versionHistoryDiffAdded}>{entry.new_content}</span>
            </div>
          )}
          <div className={classes.versionHistoryRestoreNote}>
            Restoring brings back this topic exactly as shown above.
          </div>
        </div>
      </div>
    );
  }

  // ── DELETE ──────────────────────────────────────────────────────────────
  if (entry.operation === 'delete') {
    return (
      <div className={classes.versionHistoryDiff}>
        <div className={classes.versionHistoryDiffHeader}>Removed</div>
        <div className={classes.versionHistoryDiffBody}>
          {entry.old_name && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>Question</span>
              <span className={classes.versionHistoryDiffDeleted}>{entry.old_name}</span>
            </div>
          )}
          {entry.old_content && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>Answer</span>
              <span className={classes.versionHistoryDiffDeleted}>{entry.old_content}</span>
            </div>
          )}
          <div className={classes.versionHistoryRestoreNote}>
            Restoring re-creates this topic with the content shown above.
          </div>
        </div>
      </div>
    );
  }

  // ── RESTORE ─────────────────────────────────────────────────────────────
  if (entry.operation === 'restore') {
    return (
      <div className={classes.versionHistoryDiff}>
        <div className={classes.versionHistoryDiffHeader}>Restored to</div>
        <div className={classes.versionHistoryDiffBody}>
          {restoreTarget.name && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>Question</span>
              <span className={classes.versionHistoryDiffRestored}>{restoreTarget.name}</span>
            </div>
          )}
          {restoreTarget.content && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>Answer</span>
              <span className={classes.versionHistoryDiffRestored}>{restoreTarget.content}</span>
            </div>
          )}
          <div className={classes.versionHistoryRestoreNote}>
            Restoring again will bring back the same content.
          </div>
        </div>
      </div>
    );
  }

  // ── UPDATE ──────────────────────────────────────────────────────────────
  const changedFields = fields.filter(f => f.changed);
  const unchangedFields = fields.filter(f => !f.changed && (f.newValue ?? f.oldValue));

  return (
    <div className={classes.versionHistoryDiff}>
      <div className={classes.versionHistoryDiffHeader}>
        {changedFields.length > 0 ? 'What changed' : 'No content changes'}
      </div>
      <div className={classes.versionHistoryDiffBody}>
        {changedFields.map(f => (
          <div key={f.label} className={classes.versionHistoryDiffRow}>
            <span className={classes.versionHistoryDiffLabel}>{f.label}</span>
            {f.oldValue && (
              <span className={classes.versionHistoryDiffOld}>{f.oldValue}</span>
            )}
            {f.newValue && (
              <span className={classes.versionHistoryDiffNew}>{f.newValue}</span>
            )}
          </div>
        ))}
        {unchangedFields.length > 0 && changedFields.length > 0 && (
          <div className={classes.versionHistoryRestoreNote}>
            Unchanged: {unchangedFields.map(f => f.label).join(', ')}
          </div>
        )}
        {changedFields.length === 0 && unchangedFields.map(f => (
          <div key={f.label} className={classes.versionHistoryDiffRow}>
            <span className={classes.versionHistoryDiffLabel}>{f.label}</span>
            <span className={classes.versionHistoryDiffValue}>{f.newValue ?? f.oldValue}</span>
          </div>
        ))}
        <div className={classes.versionHistoryRestorePreview}>
          <div className={classes.versionHistoryRestorePreviewTitle}>
            Restoring will bring back
          </div>
          {restoreTarget.name && (
            <div className={classes.versionHistoryDiffRow} style={{ marginBottom: '4px' }}>
              <span className={classes.versionHistoryDiffLabel}>Q</span>
              <span className={classes.versionHistoryDiffValue}>{restoreTarget.name}</span>
            </div>
          )}
          {restoreTarget.content && (
            <div className={classes.versionHistoryDiffRow}>
              <span className={classes.versionHistoryDiffLabel}>A</span>
              <span className={classes.versionHistoryDiffValue}>{restoreTarget.content}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Entry menu ──────────────────────────────────────────────────────────────

interface EntryMenuProps {
  journalUuid: string;
  isFirstEntry: boolean;
  isRestoring: boolean;
  onRestore: (journalUuid: string) => void;
  menuClass: string;
  menuBtnClass: string;
  dropdownClass: string;
  dropdownItemClass: string;
}

const EntryMenu: VFC<EntryMenuProps> = ({
  journalUuid,
  isFirstEntry,
  isRestoring,
  onRestore,
  menuClass,
  menuBtnClass,
  dropdownClass,
  dropdownItemClass,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(prev => !prev);
  };

  const handleRestore = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onRestore(journalUuid);
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={menuRef} className={menuClass} style={{ position: 'relative' }}>
      <button
        type="button"
        className={menuBtnClass}
        onClick={handleToggle}
        aria-label="Entry options"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {THREE_DOTS_SVG}
      </button>
      {open && (
        <div className={dropdownClass}>
          <button
            type="button"
            className={dropdownItemClass}
            onClick={handleRestore}
            disabled={isFirstEntry || isRestoring}
          >
            {isRestoring ? 'Restoring…' : 'Restore this version'}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Panel ───────────────────────────────────────────────────────────────────

export const VersionHistoryPanel: VFC<VersionHistoryPanelProps> = ({
  entries,
  isLoading,
  activeEntryUuid,
  onEntryClick,
  onClose,
  onRestore,
  isRestoring,
  actorNames,
}) => {
  const classes = useAgentContextStyles();
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);

  const handleEntryClick = (uuid: string) => {
    onEntryClick(uuid);
    setExpandedUuid(prev => (prev === uuid ? null : uuid));
  };

  const badgeClass = (op: KnowledgeBaseJournalEntry['operation']): string => {
    const base = classes.versionHistoryBadge;
    if (op === 'update') return `${base} ${classes.versionHistoryBadgeEdited}`;
    if (op === 'create') return `${base} ${classes.versionHistoryBadgeAdded}`;
    if (op === 'restore') return `${base} ${classes.versionHistoryBadgeRestored}`;
    return `${base} ${classes.versionHistoryBadgeDeleted}`;
  };

  return (
    <div className={classes.versionHistoryPanel}>
      <div className={classes.versionHistoryHeader}>
        <span className={classes.versionHistoryTitle}>Version history</span>
        <button
          type="button"
          className={classes.versionHistoryCloseBtn}
          onClick={onClose}
          aria-label="Close version history"
        >
          {CLOSE_ICON_SVG}
        </button>
      </div>

      <div className={classes.versionHistoryList}>
        {isLoading && (
          <p className={classes.versionHistoryEmpty}>Loading…</p>
        )}

        {!isLoading && entries.length === 0 && (
          <p className={classes.versionHistoryEmpty}>No history yet.</p>
        )}

        {!isLoading && (() => {
          // Build a lookup map so restore entries can reference their source timestamp
          const entryByUuid = new Map(entries.map(e => [e.uuid, e]));

          return entries.map((entry, index) => {
            const isActive =
              activeEntryUuid === entry.uuid ||
              (activeEntryUuid === null && index === 0);
            const isExpanded = expandedUuid === entry.uuid;
            const entryClass = `${classes.versionHistoryEntry}${
              isActive ? ` ${classes.versionHistoryEntryActive}` : ''
            }`;

            return (
              <div
                key={entry.uuid}
                className={entryClass}
                onClick={() => handleEntryClick(entry.uuid)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') handleEntryClick(entry.uuid);
                }}
              >
                {/* Row 1: date + expand chevron + three-dot menu */}
                <div className={classes.versionHistoryEntryRow1}>
                  <span className={classes.versionHistoryDate}>
                    {formatEntryDate(entry.createdAt)}
                  </span>
                  <EntryMenu
                    journalUuid={entry.uuid}
                    isFirstEntry={index === 0}
                    isRestoring={isRestoring}
                    onRestore={onRestore}
                    menuClass={classes.versionHistoryEntryMenu}
                    menuBtnClass={classes.versionHistoryMenuBtn}
                    dropdownClass={classes.versionHistoryDropdown}
                    dropdownItemClass={classes.versionHistoryDropdownItem}
                  />
                </div>

                {/* Row 2: topic name + operation badge */}
                <div className={classes.versionHistoryEntryRow2}>
                  <span className={classes.versionHistoryTopicName}>
                    {getTopicName(entry)}
                  </span>
                  <span className={badgeClass(entry.operation)}>
                    {OPERATION_LABELS[entry.operation]}
                  </span>
                </div>

                {/* Row 3: actor + restored-from reference */}
                <div>
                  <span className={classes.versionHistoryActor}>
                    {resolveActor(entry.createdBy, actorNames)}
                  </span>
                  {entry.operation === 'restore' && entry.restoredFromJournalUuid && (() => {
                    const source = entryByUuid.get(entry.restoredFromJournalUuid);
                    return source ? (
                      <span className={classes.versionHistoryRestoredFrom}>
                        {' · Restored from: '}{formatEntryDate(source.createdAt)}
                      </span>
                    ) : null;
                  })()}
                </div>

                {/* Expandable diff section */}
                {isExpanded && (
                  <div
                    onClick={e => e.stopPropagation()}
                    role="presentation"
                  >
                    <DiffCard entry={entry} classes={classes} />
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};
